import React, { useCallback, useEffect } from 'react';

import {
    ActionStatus, GamePlayerStatus,
} from '../../../Constants';
import {
    sellableRoomIndexesInStorage,
    unUpgradedRoomIndexesInStorage,
} from '../../Game/GamePlayerInfo';

import { ActionWrapper } from './ActionWrapper';
import { ResearchChoice } from './ResearchChoice';
import { ResearchInventory } from './ResearchInventory';

import './ResearchActionWindow.css';

function computeMode(gamePlayerData) {
    if (!gamePlayerData) return null;
    if (gamePlayerData.status === GamePlayerStatus.RESEARCHING_ID
        && gamePlayerData.action_status === ActionStatus.WAIT_ID) {
        return 'research';
    }
    return null;
}

// ResearchActionWindow — Research HUD for the Desktop preset. The
// FloatingWindow shell is owned by Dashboard.jsx. ResearchInventory
// is always rendered; ResearchChoice appears only in `research` mode.
// Each primitive owns its own per-row overlay artwork — the window
// does NOT pass overlay paths or render any wrapper-level overlay.
//
// The highlight-storage-event dispatch lifecycle lives here (the
// state owner) per the lessons from the Lodging coordination fix.
// The 500ms setTimeout from ResearchController.highlightStorage is
// preserved verbatim.
export function ResearchActionWindow(props) {
    const { gamePlayerData, gameData, app } = props;
    const mode = computeMode(gamePlayerData);
    const api = (app && app.game && app.game.seatedController
        && app.game.seatedController.gamePlayerApi)
        ? app.game.seatedController.gamePlayerApi : null;

    const onAmenityClick = useCallback((roomId) => {
        if (!api) return;
        // Source pattern (ResearchController.onFirstStorageClick):
        // clear before API.
        window.dispatchEvent(
            new Event('clear-storage-highlight-event'));
        api.researchBuyByRoomId(roomId);
    }, [api]);

    const onBedClick = useCallback((roomId) => {
        if (!api) return;
        window.dispatchEvent(
            new Event('clear-storage-highlight-event'));
        api.researchBuyByRoomId(roomId);
    }, [api]);

    const onStorageClick = useCallback((storageIndex) => {
        if (!api || !gamePlayerData) return;
        window.dispatchEvent(
            new Event('clear-storage-highlight-event'));
        const sellable =
            sellableRoomIndexesInStorage(gamePlayerData);
        const unupgraded =
            unUpgradedRoomIndexesInStorage(gamePlayerData);
        if (sellable.includes(storageIndex)) {
            api.researchSell(storageIndex);
        } else if (unupgraded.includes(storageIndex)) {
            api.researchUpgrade(storageIndex);
        }
    }, [api, gamePlayerData]);

    // Dispatch lifecycle — mirrors ResearchController.highlightStorage
    // (line 67-79 of source). Preserved verbatim including the 500ms
    // setTimeout. The dispatch payload is
    // [...unupgraded, ...sellable] (in that order) with the
    // storage-click callback. Effect cleanup fires clear, so
    // transitioning out of research (or unmounting the window) leaves
    // the listener clean.
    useEffect(() => {
        if (mode !== 'research') return undefined;
        if (!gamePlayerData
            || !Array.isArray(gamePlayerData.storage)) return undefined;
        const sellable =
            sellableRoomIndexesInStorage(gamePlayerData);
        const unupgraded =
            unUpgradedRoomIndexesInStorage(gamePlayerData);
        const rooms = [...unupgraded, ...sellable];
        if (!rooms.length) return undefined;
        const handle = setTimeout(() => {
            window.dispatchEvent(new CustomEvent(
                'highlight-storage-event', {
                    detail: {
                        indexes: rooms,
                        callback: onStorageClick,
                    },
                }));
        }, 500);
        return () => {
            clearTimeout(handle);
            window.dispatchEvent(
                new Event('clear-storage-highlight-event'));
        };
    }, [mode, gamePlayerData, onStorageClick]);

    const wrapperClasses = [
        'research-action-window',
        mode ? 'research-action-window--' + mode
             : 'research-action-window--idle',
    ].join(' ');

    return (
        <div className={wrapperClasses}
             data-testid={'research-action-window'}
             data-mode={mode || 'idle'}>
            <ActionWrapper>
                <ResearchInventory gamePlayerData={gamePlayerData}/>
                {mode === 'research' && (
                    <ResearchChoice
                        gamePlayerData={gamePlayerData}
                        gameData={gameData}
                        onAmenityClick={onAmenityClick}
                        onBedClick={onBedClick}/>
                )}
            </ActionWrapper>
        </div>
    );
}

export default ResearchActionWindow;
